import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Select,
  MenuItem,
  Tab,
  Tabs,
  Checkbox,
  FormControlLabel
} from "@mui/material";
import QueryParams from "../Apis/QueryParamsComponent.jsx";
import HeadersComponent from "../Apis/HeadersComponent.jsx";
import BodyComponent from "../Apis/BodyComponent.jsx";
import { useDispatch, useSelector } from "react-redux";
import LoadingOverlay from "../Common/LoadingOverlay.js";
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import coreUtils from "../../utils/coreUtils.js";
import AuthorizationComponent from "components/Collections/AuthorizationComponent.jsx";
import { getVariableSet } from "redux-store/variableSetReducers/variableSetActions.js";
import TextFieldWithVariableTooltip from "components/Common/TextFieldWithVariableTooltip.jsx";
import { setIsSaveRequest } from 'redux-store/variableSetReducers/variableSetSlice';
import { Code, Eye } from "lucide-react";

const TestCaseSetRequest = ({
  testCase,
  onSave,
  isDisabledActions,
  disableUrl,
  selectedEnvironmentId
}) => {
  const dispatch = useDispatch();
  const [method, setMethod] = useState("");
  const [url, setUrl] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [queryParams, setQueryParams] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [authorization, setAuthorization] = useState({});
  const [body, setBody] = useState({});
  const [bodyType, setBodyType] = useState([]);
  const [curlCommand, setCurlCommand] = useState("");
  const isSaving = useSelector(
    (state) => state.testCaseSet.isSaving
  );
  const tabLabels = ["Params", "Authorization", "Headers", "Body"];
  const defaultHeaders = [
    { name: "Accept", value: "*/*", readOnly: true },
    { name: "Accept-Encoding", value: "identity", readOnly: true },
    { name: "Connection", value: "keep-alive", readOnly: true },
  ];
  const saveButtonRef = useRef(null);
  const [isRequestBodyChanged, setIsRequestBodyChanged] = useState(false);
  const [saveRequestBodyChanges, setSaveRequestBodyChanges] = useState(false);
  const isSaveRequest = useSelector((state) => state.variableSet.isSaveRequest);
  const [view, setView] = useState("code");

  // Centralized change tracking
  useEffect(() => {
    const hasChanged =
      url !== (testCase?.url || "") ||
      method !== (testCase?.request_type || "") ||
      JSON.stringify(queryParams) !== JSON.stringify(testCase?.params || []) ||
      JSON.stringify(headers) !== JSON.stringify(testCase?.headers || [...defaultHeaders]) ||
      JSON.stringify(authorization) !== JSON.stringify(testCase?.authorization) ||
      JSON.stringify(body) !== JSON.stringify(testCase?.body || {}) ||
      bodyType !== (testCase?.body_type || "");

    setIsRequestBodyChanged(hasChanged);
  }, [url, method, queryParams, headers, authorization, body, bodyType]);

  useEffect(() => {
    const extractedParams = updateParamsFromUrl(url);
    setQueryParams(extractedParams);
  }, [url]);

  useEffect(() => {
    const updateSaveRequest = async () => {
      await handleSaveRequest();
      await dispatch(setIsSaveRequest(false));
    }

    if (isSaveRequest) updateSaveRequest();
  }, [isSaveRequest]);

  const handleView = (event, selectedView) => {
    setView(selectedView);
  }

  useEffect(() => {
    setInitialState();
  }, []);

  const fetchSelectedEnvironment = async () => {
    if (selectedEnvironmentId) {
      const result = await dispatch(getVariableSet({
        variableSetId: selectedEnvironmentId
      })).unwrap();
    }
  };

  useEffect(() => {
    fetchSelectedEnvironment();
  }, [selectedEnvironmentId]);

  const setInitialState = async () => {
    setUrl(testCase?.url || "");
    setMethod(testCase?.request_type || "");
    handleParamsChange([...(testCase?.params || [])]);
    setBody(testCase?.body || {});
    setBodyType(testCase?.body_type || "");
    setHeaders(testCase?.headers || [...defaultHeaders]);
    setAuthorization(testCase?.authorization);
    setCurlCommand(testCase?.curl || "");
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleMethodChange = (event) => {
    const newMethod = event.target.value;
    setMethod(newMethod);
  };

  const handleUrlChange = (event) => {
    setUpdatedQueryParams(event.target.value);
  };

  const updateParamsFromUrl = (requestUrl) => {
    return coreUtils.extractParamsFromUrl(requestUrl);
  };

  const setUpdatedQueryParams = async (url, params) => {
    const extractedParams = params ? params : updateParamsFromUrl(url);
    setUrl(url);
    setQueryParams(extractedParams);
  }

  const concatQueryParams = (rows) => {
    const tmpUrl = url || testCase?.url || "";
    const validParams = rows.filter((p) => p.name);
    const tParams = buildQueryParams(validParams);
    const indexOfQuery = tmpUrl.indexOf("?");
    const baseUrl =
      indexOfQuery > -1 ? tmpUrl.substring(0, indexOfQuery) : tmpUrl;
    const newUrl = `${baseUrl}${tParams.length ? "?" + tParams : ""}`;
    setUpdatedQueryParams(newUrl, validParams);
  };

  const buildQueryParams = (arr) => {
    return arr
      .map((item) => (item.value ? `${item.name}=${item.value}` : item.name))
      .join("&")
      .replace(/&+$/, "");
  };

  const handleSaveRequest = async () => {
    onSave({
      url: url,
      request_type: method,
      body: body || null,
      body_type: bodyType || null,
      params: queryParams || [],
      headers: headers || [],
      authorization_type: authorization?.authorization_type,
      authorization_value: authorization?.authorization_value,
      also_update_entity: saveRequestBodyChanges,
      use_local_agent: localStorage.getItem("runOn") === "localAgent"
    });
  };

  const handleUrlTextFieldBlur = (event) => {
    event.preventDefault();
  };

  const handleParamsChange = (newParams) => {
    concatQueryParams(newParams);
  };

  const handleHeadersChange = async (validHeaders) => {
    setHeaders(validHeaders);
  };

  const onAuthorizationUpdate = async (validAuthorization) => {
    setAuthorization(validAuthorization);
  };

  const handleBodyChange = async (body, bodyType) => {
    setBody(body);
    setBodyType(bodyType);
  };

  const getTabStyle = (index) => ({
    textTransform: "none",
    color: activeTab === index ? "var(--text-primary)" : "var(--text-secondary)",
    backgroundColor: activeTab === index ? "#D27FFF" : "var(--text-primary)",
  });

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      saveButtonRef?.current?.click();
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <QueryParams
            initialParams={queryParams}
            onParamsChange={handleParamsChange}
            skipOutsideClickCheck={true}
            hideExtraFields={true}
          />
        );
      case 1:
        return (
          <AuthorizationComponent
            allowInheritFromParent={false}
            authorization={authorization}
            onAuthorizationUpdate={onAuthorizationUpdate}
          />
        );
      case 2:
        return (
          <HeadersComponent
            headers={headers}
            onHeadersChange={handleHeadersChange}
            hideExtraFields={true}
          />
        );
      case 3:
        return (
          <BodyComponent
            url={url}
            body={body}
            headers={headers}
            bodyType={bodyType}
            onBodyChange={handleBodyChange}
            hideExtraFields={true}
          />
        );
      default:
        return null;
    }
  };

  const tabStyle = {
    height: "32px",
    minHeight: "32px",
    textTransform: "none",
    color: "var(--text-secondary)",
    backgroundColor: "transparent",
  };

  const getColorByMethod = (method) => {
    switch (method) {
      case "GET":
        return "green";
      case "POST":
        return "#ffb900";
      case "PUT":
        return "blue";
      case "PATCH":
        return "purple";
      case "DELETE":
        return "red";
      case "HEAD":
        return "green";
      case "OPTIONS":
        return "pink";
      default:
        return "var(--text-primary)";
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "520px",
        p: 1.5,
        bgcolor: "var(--background-primary)",
        borderRadius: "8px",
        border: "1px solid var(--borderColor)"
      }}
    >
      {(isSaving) && (<LoadingOverlay isLoading={isSaving} />)}
      <div className="flex flex-row justify-between items-center mb-2">
        <h6 style={{ color: "var(--text-primary)" }}>Request Data</h6>
        <div className='flex items-center gap-2'>
          {isRequestBodyChanged && (
            <div className="flex flex-row items-center">
              <FormControlLabel
                control={<Checkbox checked={saveRequestBodyChanges} onChange={(e) => setSaveRequestBodyChanges(e.target.checked)} />}
                label="Save Changes to API Details"
              />
            </div>)}
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={handleView}
            aria-label="View"
            className='my-2'
            sx={{
              borderRadius: '4px',
              backgroundColor: 'var(--background-layout)',
              padding: '4px',
              '& .MuiToggleButton-root': {
                border: 'none',
                borderRadius: '3px !important',
                margin: '0',
                color: 'var(--text-primary)',
                '&.Mui-selected': {
                  backgroundColor: 'var(--background-primary)',
                  color: "var(--primary-color)",
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  '&:hover': {
                    backgroundColor: 'var(--background-primary)'
                  }
                },
                '&:not(.Mui-selected)': {
                  '&:hover': {
                    backgroundColor: 'var(--background-layout)'
                  }
                }
              }
            }}
          >
            <ToggleButton value="code" aria-label="Code" className='px-3 py-1'>
              <Code size={16} />
            </ToggleButton>
            <ToggleButton value="preview" aria-label="Preview" className='px-3 py-1'>
              <Eye size={16} />
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
      </div>
      {view === "preview" ? (
        <>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Select
              size="small"
              value={method}
              onChange={handleMethodChange}
              sx={{
                marginRight: "10px", minWidth: 110,
                color: getColorByMethod(method),
                '& .MuiSelect-select': {
                  color: getColorByMethod(method),
                  fontWeight: "bold",
                },
              }}
              disabled={disableUrl}
            >
              <MenuItem value="GET" sx={{ color: "green", fontWeight: "bold" }}>
                GET
              </MenuItem>
              <MenuItem value="POST" sx={{ color: "#ffb900", fontWeight: "bold" }}>
                POST
              </MenuItem>
              <MenuItem value="PUT" sx={{ color: "blue", fontWeight: "bold" }}>
                PUT
              </MenuItem>
              <MenuItem value="PATCH" sx={{ color: "purple", fontWeight: "bold" }}>
                PATCH
              </MenuItem>
              <MenuItem value="DELETE" sx={{ color: "red", fontWeight: "bold" }}>
                DELETE
              </MenuItem>
              <MenuItem value="HEAD" sx={{ color: "green", fontWeight: "bold" }}>
                HEAD
              </MenuItem>
              <MenuItem value="OPTIONS" sx={{ color: "pink", fontWeight: "bold" }}>
                OPTIONS
              </MenuItem>
            </Select>
            <TextFieldWithVariableTooltip
              value={url}
              onChange={handleUrlChange}
              onKeyDown={handleKeyDown}
              onBlur={handleUrlTextFieldBlur}
              variant="outlined"
              size="small"
              fullWidth
              sx={{ marginRight: "10px" }}
              disabled={disableUrl}
            />
            <Button
              variant="contained"
              ref={saveButtonRef}
              onClick={handleSaveRequest}
              size="medium"
              disabled={isDisabledActions || disableUrl}
              sx={{
                marginLeft: "10px"
              }}
            >
              Save
            </Button>
          </Box>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            sx={{
              marginTop: "25px",
              height: "32px",
              minHeight: "32px",
              border: "1px solid var(--borderColor)",
              borderRadius: "5px",
            }}
          >
            {tabLabels.map((label) => (
              <Tab
                key={label}
                label={label}
                sx={getTabStyle(label)}
                style={tabStyle}
              />
            ))}
          </Tabs>
          <Box
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              marginTop: "10px",
              marginBottom: "10px",
            }}
          >
            {renderTabContent()}
          </Box>
        </>
      ) : (
        <Box sx={{ mt: 2 }}>
          <pre className="break-all" style={{
            backgroundColor: 'var(--background-layout)',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid var(--borderColor)',
            fontSize: '14px',
            fontFamily: 'monospace',
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            color: 'var(--text-primary)',
            height: "420px",
            maxHeight: "420px",
          }}>
            {curlCommand}
          </pre>
        </Box>
      )}

      <LoadingOverlay isLoading={isLoading} />
    </Box>
  );
};

export default TestCaseSetRequest;
